import { theme as antdAlgorithms } from 'antd';

import { buildPalette } from './themes';

/**
 * The antd v5 theme for a keepr theme + mode, built from the same palette
 * src/css/themes.css is generated from. CSS owns the custom components;
 * this owns everything antd renders internally, and neither can drift from
 * the other because both read src/config/themes.js.
 *
 * The dark algorithm does the derivation antd needs internally (hover
 * states, masks, disabled fills); the explicit tokens below then pin the
 * surfaces, ink, and borders back to keepr's own palette so the two halves
 * of a screen — an antd Select and a keepr panel — are the same greys.
 */
export function buildAntdTheme(themeKey, mode) {
	const p = buildPalette(themeKey, mode);
	const dark = mode === 'dark';

	return {
		algorithm: dark ? antdAlgorithms.darkAlgorithm : antdAlgorithms.defaultAlgorithm,
		token: {
			colorPrimary: p['--action'],
			colorPrimaryHover: p['--action-hover'],
			colorPrimaryActive: p['--action-hover'],
			colorPrimaryBg: p['--action-tint'],
			colorPrimaryBgHover: p['--action-tint-soft'],
			colorLink: p['--action'],
			colorLinkHover: p['--action-hover'],
			colorLinkActive: p['--action-deep'],
			// What sits on a filled primary button. In light that is white;
			// in dark the accent is bright enough that white on it fails, so
			// the palette hands back a near-black instead.
			colorTextLightSolid: p['--on-action'],

			colorText: p['--ink'],
			colorTextSecondary: p['--ink-muted'],
			colorTextTertiary: p['--ink-muted'],
			// Quaternary is placeholders/disabled glyphs — decoration, so the
			// sub-readable grey is allowed here (never for real text).
			colorTextQuaternary: p['--decor'],

			colorBorder: p['--border-control'],
			colorBorderSecondary: p['--border'],
			colorSplit: p['--line-soft'],

			colorBgLayout: p['--ground'],
			colorBgContainer: p['--surface'],
			colorBgElevated: p['--surface-raised'],
			colorBgMask: p['--scrim'],
			colorFillSecondary: p['--chip-bg'],
			colorFillTertiary: p['--chip-bg'],
			colorFillQuaternary: p['--hover-bg'],

			colorError: p['--danger'],
			colorErrorHover: p['--danger-hover'],
			colorErrorBg: p['--danger-tint'],
			colorErrorBorder: p['--danger-border'],
			colorWarning: p['--warning'],
			colorWarningBg: p['--warning-tint'],
			colorWarningBorder: p['--warning-border'],
			colorSuccess: p['--success'],
			colorSuccessBg: p['--success-tint'],
			colorSuccessBorder: p['--success-border'],
			colorInfo: p['--info'],
			colorInfoBg: p['--info-tint'],
			colorInfoBorder: p['--info-border'],

			fontFamily: "'Instrument Sans', system-ui, sans-serif",
			fontSize: 14,

			borderRadius: 8,
			borderRadiusLG: 10,
			borderRadiusSM: 7,

			controlHeight: 32,
			controlHeightLG: 34, // inputs may be 34px
			controlHeightSM: 26,

			// Focus ring on inputs: action border + soft action glow.
			controlOutline: p['--action-ring'],
			controlOutlineWidth: 3,

			boxShadow: p['--shadow-elevated'],
			boxShadowSecondary: p['--shadow-elevated'],
		},
		components: {
			Button: {
				fontSize: 13,
				fontWeight: 600,
				defaultBorderColor: p['--border-btn'],
				defaultColor: p['--ink'],
				defaultBg: p['--surface'],
			},
			Tag: {
				defaultBg: p['--chip-bg'],
				defaultColor: p['--ink'],
				borderRadiusSM: 7,
			},
			Segmented: {
				// One joined outline; the selected segment is ink with the
				// opposite ink on top (the neutral view switcher overrides to
				// the chip grey in controls.css).
				itemSelectedBg: p['--ink'],
				itemSelectedColor: p['--on-ink'],
				itemColor: p['--ink'],
				itemHoverColor: p['--ink'],
				itemHoverBg: p['--hover-bg'],
				trackBg: p['--surface'],
				trackPadding: 0,
				borderRadius: 8,
				borderRadiusSM: 7,
			},
			Switch: {
				trackHeight: 22,
				trackMinWidth: 38,
				handleSize: 18,
			},
			Tabs: {
				itemSelectedColor: p['--ink'],
				inkBarColor: p['--action'],
				itemHoverColor: p['--ink'],
			},
			Table: {
				headerBg: p['--surface'],
				rowHoverBg: p['--hover-bg'],
				borderColor: p['--border'],
			},
			Layout: {
				siderBg: p['--rail-bg'],
				bodyBg: p['--ground'],
				headerBg: p['--surface'],
			},
			Modal: {
				contentBg: p['--surface-raised'],
				headerBg: p['--surface-raised'],
			},
			Card: {
				colorBgContainer: p['--surface'],
			},
			Input: {
				colorBgContainer: p['--field-bg'],
			},
			Select: {
				optionSelectedBg: p['--action-tint'],
				optionSelectedColor: p['--action-deep'],
			},
			Dropdown: {
				controlItemBgHover: p['--hover-bg'],
			},
			Menu: {
				itemSelectedBg: p['--action-tint'],
				itemSelectedColor: p['--action-deep'],
				itemHoverBg: p['--hover-bg'],
			},
			Tooltip: {
				colorBgSpotlight: p['--ink'],
				colorTextLightSolid: p['--on-ink'],
			},
		},
	};
}

export default buildAntdTheme;
